import React, { useMemo } from 'react';
import { Flex, FormControl, FormLabel, Grid, Heading, Input, useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import DataConsentCheckbox from 'ts/common/components/DataConsentCheckbox';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import BookingSessionHeading from 'client_react/booking/components/BookingSessionHeading';
import useBookingFlowSteps from 'client_react/booking/hooks/useBookingFlowSteps';
import {
    isBookingSessionError,
    isBookingSessionTypeUnavailableError
} from 'client_react/booking/useBookingSession';

interface ContactFormFields {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const ContactInformationPage = () => {
    const {
        formState: { isSubmitting, isValid },
        handleSubmit,
        register
    } = useForm<ContactFormFields>({ mode: 'onTouched' });

    const { bookingSession, bookingSessionType, isPending, updateBookingSession } =
        useClientBookingExperience();

    const { goToPreviousStep, goToNextStep } = useBookingFlowSteps();

    const t = useTranslate('booking.contactInformationPage');
    const bookingT = useTranslate('booking');

    const errorToast = useToast({
        status: 'error'
    });

    const handleSubmitForm = handleSubmit(async (contactFieldValues: ContactFormFields) => {
        if (!bookingSessionType || !bookingSession) {
            return;
        }

        const response = await updateBookingSession({
            sessionTypePublicId: bookingSessionType.publicId,
            contact: {
                type: 'contact',
                ...contactFieldValues
            }
        });

        if (!response || isBookingSessionError(response)) {
            errorToast({
                title: isBookingSessionTypeUnavailableError(response)
                    ? bookingT('sessionTypeUnavailable')
                    : t('apiError')
            });

            return;
        }

        goToNextStep();
    });

    const buttons = useMemo<ButtonProps[]>(
        () => [
            {
                text: t('back'),
                onClick: () => goToPreviousStep(),
                variant: 'outline primary'
            },
            {
                text: t('continue'),
                isDisabled: isPending || isSubmitting || !isValid,
                isLoading: isSubmitting,
                variant: 'primary',
                type: 'submit'
            }
        ],
        [goToPreviousStep, isPending, isSubmitting, isValid, t]
    );

    if (!bookingSession || !bookingSessionType) {
        return null;
    }

    return (
        <>
            <BookingSessionHeading
                bookingSession={bookingSession}
                bookingSessionType={bookingSessionType}
            />
            <Flex as="form" direction="column" gap="24px" onSubmit={handleSubmitForm}>
                <Heading as="h3" size="lg" margin={0}>
                    {t('heading')}
                </Heading>
                <Grid gap="16px 24px" gridTemplateColumns="repeat(auto-fill, minmax(400px, 1fr))">
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('firstName')}</FormLabel>
                        <Input {...register('firstName', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('lastName')}</FormLabel>
                        <Input {...register('lastName', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('email')}</FormLabel>
                        <Input type="email" {...register('email', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('phone')}</FormLabel>
                        <Input type="tel" {...register('phone', { required: true })} />
                    </FormControl>
                </Grid>
                {bookingSession.isSubjectToGdpr && (
                    <DataConsentCheckbox register={register} translate={t} />
                )}
                <ResponsiveButtonGroup buttons={buttons} />
            </Flex>
        </>
    );
};

export default ContactInformationPage;
